import React, { useState } from "react";
import {
  Container,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  LinearProgress,
} from "@mui/material";
import SnackbarShow from "../SnackbarShow";
import { userBulkCreateImport } from "../../API/APICalls";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ValidationResponseComp from "../ValidationResponseComp";
import ProgressBarShow from "../ProgressBarShow";
import {
  allowedExtensions,
  requiredHeadersForBulkCreate,
  sampleBulkCreateData,
  createUserKeypoints,
} from "../../Utils/Constants";
import { userCreateImportFormValidataion } from "../../Utils/UserValidations";
import {
  decideBatchSize,
  createBatches,
  validateLocalPart,
  validateQuotaForCreateBulk,
} from "../../Utils/HelperFunctions";
import UpdateResponseComp from "../UpdateResponseComp";
import DomainDetailsDialog from "../DomainDetailsDialog";

const UserCreateImportForm = ({ id, domainDetails }) => {
  const [snackbarMessage, setSnackbarMessage] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [progress, setProgress] = useState(0);
  const [uploadResponseMessage, setUploadResponseMessage] = useState([]);
  const [file, setFile] = useState();
  const [csvData, setCsvData] = useState("");
  const [validationError, setValidationError] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [disableUploadButton, setDisableUploadButton] = useState(true);
  const [disableValidateButton, setDisableValidateButton] = useState(true);
  const [domainDeatilsOpen, setDomainDetailsOpen] = useState(false);

  const handleDomainDetailsOpen = () => {
    setDomainDetailsOpen(!domainDeatilsOpen);
  };

  const handleFileChange = (e) => {
    setFile(null);
    setDisableUploadButton(true);
    if (e.target.files.length) {
      const inputFile = e.target.files[0];
      if (!allowedExtensions.includes(inputFile?.type)) {
        setSnackbarMessage({
          open: true,
          message: "Please upload CSV/XSLX file to update data.",
          severity: "error",
        });
        setDisableValidateButton(true);
        return;
      }
      setFile(inputFile);
      setDisableValidateButton(false);
    }
  };

  const cleanUpData = (data) => {
    const cleanedData = data
      .filter((row) => {
        return Object.values(row).some(
          (value) => value !== "" && value !== null && value !== undefined
        );
      })
      .map((row) => {
        const trimmedRow = {};
        for (const key in row) {
          if (typeof row[key] === "string") {
            trimmedRow[key] = row[key].trim();
          } else {
            trimmedRow[key] = row[key];
          }
        }
        return trimmedRow;
      });

    const finalData = cleanedData.filter(
      (row) =>
        row["local_part"] !== undefined &&
        row["local_part"] !== null &&
        row["local_part"] !== ""
    );

    const uniqueParsedData = [
      ...new Map(finalData.map((item) => [item["local_part"], item])).values(),
    ];
    return uniqueParsedData;
  };

  const validateData = async (data) => {
    const validatedData = [];
    const validationErrors = [];
    const csvFileHeaders = Object.keys(data[0]);
    const isRequiredHeadersPresent = requiredHeadersForBulkCreate.every(
      (element) => csvFileHeaders.includes(element)
    );
    if (!isRequiredHeadersPresent) {
      setSnackbarMessage({
        open: true,
        message:
          "The imported CSV file header does not match the required format.",
        severity: "error",
      });
      setDisableUploadButton(true);
      return { validatedData, validationError };
    }
    for (const row of data) {
      try {
        if (row.country_info !== null && row.country_info !== undefined) {
          if (typeof row.country_info === "number") {
            row.country_info = [row.country_info];
          }
          if (typeof row.country_info === "string") {
            row.country_info = row.country_info.split(",").map(Number);
          }
        }
        await userCreateImportFormValidataion.validate(row);
        if (row.password) {
          row.password = btoa(row.password);
        }
        if (row.mobile) {
          row.mobile = row.mobile.toString();
        }
        if (typeof row.employee_id === "number") {
          row.employee_id = row.employee_id.toString();
        }
        if (row.local_part) {
          await validateLocalPart(id, row.domain_id, row.local_part);
        }
        validatedData.push(row);
      } catch (error) {
        validationErrors.push({
          rowIndex: data.indexOf(row) + 1,
          username: row.local_part,
          errors: Array.isArray(error.errors)
            ? error.errors
            : [String(error.message || error)],
        });
      } finally {
        setDisableUploadButton(false);
      }
    }
    return { validatedData, validationErrors };
  };

  // Process CSV file data.
  const processCSVData = (file) => {
    try {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: true,
        complete: async (results) => {
          const cleanedData = cleanUpData(results.data);
          const { validatedData, validationErrors } = await validateData(
            cleanedData
          );
          if (validatedData.length === 0) {
            setSnackbarMessage({
              open: true,
              message:
                "No valid data found in CSV. Empty local_part entries removed.",
              severity: "error",
            });
            setDisableUploadButton(true);
          }
          console.log(validatedData);
          setCsvData(validatedData);
          setValidationError(validationErrors);
          setValidationLoading(false);
          setDisableUploadButton(false);
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  // Process XLSX file data.
  const processXLSXData = (file) => {
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 0,
          raw: true,
          defval: null,
        });
        console.log(jsonData);
        // const cleanedData = cleanUpData(jsonData);
        const { validatedData, validationErrors } = await validateData(
          jsonData
        );
        console.log(validatedData.length);
        if (validatedData.length === 0) {
          setSnackbarMessage({
            open: true,
            message:
              "No valid data found in ELSX. Empty local_part entries removed.",
            severity: "error",
          });
          setDisableUploadButton(true);
        }
        setCsvData(validatedData);
        setValidationError(validationErrors);
        setValidationLoading(false);
        setDisableUploadButton(false);
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error(error);
    }
  };

  const handleValidateButton = () => {
    setCsvData("");
    setValidationError("");
    setDisableUploadButton(true);
    if (!file) {
      setSnackbarMessage({
        open: true,
        message: "Please upload CSV/ELSX file to validate data.",
        severity: "error",
      });
      setDisableUploadButton(true);
      return;
    }
    setValidationLoading(true);
    if (file.type === "text/csv") {
      processCSVData(file);
    } else if (
      file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      processXLSXData(file);
    } else {
      setSnackbarMessage({
        open: true,
        message: "Unsupported file type. Please upload a CSV or XLSX file.",
        severity: "error",
      });
      setDisableUploadButton(true);
    }
  };

  const checkDomainQuota = async (data) => {
    const groupedDomainQuota = data.reduce((acc, item) => {
      if (!acc[item.domain_id]) {
        acc[item.domain_id] = {
          domain_id: item.domain_id,
          domain_name: item.domain_name,
          allocated_space: 0,
        };
      }
      acc[item.domain_id].allocated_space += item.allocated_space;
      return acc;
    }, {});
    const domains = Object.values(groupedDomainQuota);
    for (const domain of domains) {
      await validateQuotaForCreateBulk(
        id,
        domain.domain_id,
        domain.allocated_space,
        domain.domain_name
      );
    }
  };

  const handleUploadData = async () => {
    if (csvData) {
      try {
        await checkDomainQuota(csvData);
        setValidationError("");
        const batches = createBatches(csvData, decideBatchSize(csvData.length));
        let completedBatches = 0;
        for (const batch of batches) {
          setDisableUploadButton(true);
          try {
            const response = await userBulkCreateImport(id, batch);
            completedBatches += 1;
            const newProgress = (completedBatches / batches.length) * 100;
            setProgress(newProgress);
            setUploadResponseMessage((prevResponses) => [
              ...prevResponses,
              {
                batchId: batches.indexOf(batch) + 1,
                message: response.data || "Batch updated successfully!",
              },
            ]);
          } catch (error) {
            setUploadResponseMessage((prevResponses) => [
              ...prevResponses,
              {
                batchId: batches.indexOf(batch) + 1,
                message: { email: [error.message] },
              },
            ]);
            console.error(error.message);
          } finally {
            setDisableUploadButton(false);
          }
        }
      } catch (error) {
        let errorMessage = error || "An unknown error occurred.";
        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === "string") {
          errorMessage = error;
        }
        setSnackbarMessage({
          open: true,
          message: errorMessage,
          severity: "error",
        });
      }
    }
  };

  const handleSampleCsvDownload = () => {
    const csv = Papa.unparse(sampleBulkCreateData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "sample_user_bulk_create.csv");
  };

  const handleSampleXLSXDownload = () => {
    const worksheet = XLSX.utils.json_to_sheet(sampleBulkCreateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(data, "sample_user_bulk_create.xlsx");
  };

  const sortedDomains =
    domainDetails.domain_ids !== undefined && domainDetails.domain_ids !== null
      ? Object.entries(domainDetails.domain_ids).sort((a, b) =>
          a[1].localeCompare(b[1])
        )
      : null;

  const domainDetailsDaialogComp = domainDeatilsOpen ? (
    <DomainDetailsDialog
      open={domainDeatilsOpen}
      handleClose={handleDomainDetailsOpen}
      sortedDomains={sortedDomains}
    />
  ) : null;

  const validationLoader = validationLoading ? (
    <Container
      style={{
        gap: 15,
        width: "85vh",
        margin: "0 auto",
        padding: "2rem",
        boxShadow: 3,
      }}
    >
      <LinearProgress />
    </Container>
  ) : null;

  const snackbarMessageComp = snackbarMessage.open ? (
    <SnackbarShow
      data={snackbarMessage}
      setSnackbarMessage={setSnackbarMessage}
    />
  ) : null;

  return (
    <>
      {domainDetailsDaialogComp}
      {snackbarMessageComp}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box sx={{ width: "85vh", p: 2 }}>
          <Typography variant="h5" gutterBottom>
            Mailbox bulk user creation using CSV/XLSX file.
          </Typography>
          <Typography variant="h6" gutterBottom sx={{ color: "green" }}>
            Key points for CSV/XLSX file upload:-
          </Typography>
          <ul>
            {createUserKeypoints.map((point, index) => (
              <li key={index}>
                <Typography
                  gutterBottom
                  sx={{ padding: "2px" }}
                  variant="body2"
                >
                  {point}
                </Typography>
                {index !== createUserKeypoints.length - 1 && <Divider />}
              </li>
            ))}
          </ul>
          <Button
            style={{ margin: "10px" }}
            variant="contained"
            size="small"
            onClick={handleSampleCsvDownload}
          >
            Download Sample CSV
          </Button>
          <Button
            style={{ margin: "10px" }}
            variant="contained"
            size="small"
            onClick={handleSampleXLSXDownload}
          >
            Download Sample XLSX
          </Button>
        </Box>
        <Box
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            width: "85vh",
            p: 2,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={handleDomainDetailsOpen}
          >
            Get domain_id and domain_names
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            width: "85vh",
            p: 2,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <TextField
            name="file"
            type="file"
            size="small"
            onChange={handleFileChange}
          />
          <Button
            variant="contained"
            onClick={handleValidateButton}
            disabled={disableValidateButton}
          >
            Validate
          </Button>
          <Button
            variant="contained"
            onClick={handleUploadData}
            disabled={disableUploadButton}
          >
            Upload
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              window.location.reload();
            }}
          >
            Clear
          </Button>
        </Box>
      </Box>
      {validationLoader}
      {progress > 0 && (
        <Container
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 15,
            width: "85vh",
            margin: "0 auto",
            padding: "2rem",
            boxShadow: 3,
          }}
        >
          <ProgressBarShow progress={progress} />
        </Container>
      )}
      {validationError && (
        <ValidationResponseComp
          data={validationError}
          success_count={csvData.length}
        />
      )}
      {uploadResponseMessage.length > 0 && (
        <UpdateResponseComp data={uploadResponseMessage} />
      )}
    </>
  );
};

export default UserCreateImportForm;
